import { describeOutcome, planWrites, type ExistingAssignment, type PlannedWrite, type WriteOutcome } from "./assign";

/*
 * ASSIGNING A PROGRAM IS A STORED PLAN FOR CALLING ONE RPC, AND NOTHING ELSE.
 * `docs/PROGRAMS-DESIGN.md` §3. A program holds *which template, which week, which weekday*; this
 * module turns that into dates and calls the existing `assign_coach_workout` once per day, in
 * order, so every assigned day is a client-scoped COPY of its template exactly as a hand
 * assignment is. One source (2026-08-27), copy-on-assign (V7-2), both unchanged.
 *
 * 🔴 THERE IS NO SECOND ASSIGN HERE, AND THAT IS THE WHOLE POINT. `assign.ts`'s own header refuses
 * a second place where "assign" is decided; this module asks that file's `planWrites` for what is
 * already there and its `describeOutcome` for what happened, so a program's prediction cannot
 * disagree with a single assignment's. A `coach_assignments` insert written here would be the
 * drift the design refuses, and a source ruler asserts this file names no write verb but the RPC.
 */

/** One reference from `coach_program_days`, as PostgREST returns it. */
export type ProgramDayRow = {
  weekIndex: number;
  /** 1 = Monday, matching the phone's Monday-first calendar. Never the locale's `startOfWeek`. */
  weekday: number;
  position: number;
  /** The TEMPLATE's id. `assign_coach_workout` clones it; the copy is what the client gets. */
  workoutId: string;
  note: string | null;
};

/** One planned call, in the order the writer will make it. */
export type ProgramWrite = {
  dateISO: string;
  workoutId: string;
  note: string;
  action: PlannedWrite["action"];
};

/**
 * `start + (week_index − 1) × 7 + (weekday − 1)`, in UTC, as an ISO date.
 *
 * ⚠️ UTC ARITHMETIC ON PURPOSE. A `new Date("2026-09-21")` is midnight UTC; adding days with
 * `setDate` on a LOCAL date crosses a DST boundary twice a year and lands the second half of a
 * block one day out — a whole month of a coach's work off by one, in two weeks of the year, which
 * is exactly the kind of defect nobody reproduces on the day they look for it.
 */
export function dateForCell(startMondayISO: string, weekIndex: number, weekday: number): string {
  const start = new Date(`${startMondayISO}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) throw new Error(`not a date: ${startMondayISO}`);
  const offset = (weekIndex - 1) * 7 + (weekday - 1);
  const d = new Date(start.getTime() + offset * 86_400_000);
  return d.toISOString().slice(0, 10);
}

/** Is this ISO date a Monday? The CHECK is the belt; this is the picker's braces. */
export function isMonday(dateISO: string): boolean {
  const d = new Date(`${dateISO}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.getUTCDay() === 1;
}

/**
 * The plan, in write order.
 *
 * ⚠️ THE ROWS ARE SORTED HERE AND NOT TRUSTED TO ARRIVE SORTED. A PostgREST embed comes back
 * UNORDERED (b4-2/3/10, paid for twice), and the tests shuffle the fixture on purpose so that a
 * dropped sort is a red rather than a coincidence. Order matters twice over: the writer is
 * sequential, and the coach reads the outcomes in it.
 *
 * ⚠️ AND `planWrites` IS ASKED PER TEMPLATE, not once for the whole plan, because its question is
 * "is THIS source workout already on THIS date" — the same question, keyed the same way, that the
 * unique index will ask. One template on two weekdays is two independent questions.
 */
export function planProgram(
  rows: readonly ProgramDayRow[],
  startMondayISO: string,
  existing: readonly ExistingAssignment[],
): ProgramWrite[] {
  const ordered = [...rows].sort(
    (a, b) => a.weekIndex - b.weekIndex || a.weekday - b.weekday || a.position - b.position,
  );
  return ordered.map((r) => {
    const dateISO = dateForCell(startMondayISO, r.weekIndex, r.weekday);
    const [planned] = planWrites([dateISO], r.workoutId, [...existing]);
    return {
      dateISO,
      workoutId: r.workoutId,
      // The RPC's generated signature is non-nullable (`components/AssignWorkoutWindow.tsx` sends '' too; `AssignPanel.tsx` did until W63), so a null note is ''.
      note: r.note ?? "",
      action: planned!.action,
    };
  });
}

/** The three numbers the assignment record carries, and the CHECK they must satisfy. */
export type Tally = { planned: number; written: number; already: number };

/**
 * `days_written + days_already = days_planned`, mirrored so the SCREEN refuses to record a
 * half-block rather than discovering it at the insert.
 *
 * 🔴 A LOOP THAT STOPPED HALFWAY MUST RECORD NOTHING. The database's CHECK makes the bad row
 * unrepresentable; this makes the refusal legible before the round trip, and the two agree
 * because this function states the same equation rather than a summary of it. A REFUSED day
 * counts toward neither side — which is why a partial run fails this and must be resumed.
 */
export function tallyAddsUp(t: Tally): boolean {
  return t.written >= 0 && t.already >= 0 && t.planned >= 0 && t.written + t.already === t.planned;
}

/** Count a finished run's outcomes into the record's three numbers. */
export function tallyOf(outcomes: readonly WriteOutcome[]): Tally {
  return {
    planned: outcomes.length,
    written: outcomes.filter((o) => o.kind === "assigned").length,
    already: outcomes.filter((o) => o.kind === "alreadyAssigned").length,
  };
}

/** What one call to the RPC does, injected so the writer can be driven by a test. */
export type AssignCall = (write: ProgramWrite) => Promise<{ error: { message: string } | null }>;

/**
 * Write the plan, ONE CALL AT A TIME.
 *
 * 🔴 NEVER `Promise.all`, AND THE REASON IS THE DATABASE'S. Two clones racing is the shape the
 * unique index arbitrates, so a parallel run turns a predictable `alreadyAssigned` into a refusal
 * whose cause is the coach's own other call. The AssignPanel made this decision first; a program
 * is the same decision sixteen times over.
 *
 * ⚠️ A DAY PREDICTED `alreadyAssigned` IS NOT ATTEMPTED. The prediction can be stale — the
 * database stays the authority, and a stale prediction shows up as a 23505 that `describeOutcome`
 * translates back into `alreadyAssigned`, so both routes reach the same word.
 */
export async function writeProgram(
  plan: readonly ProgramWrite[],
  call: AssignCall,
): Promise<WriteOutcome[]> {
  const outcomes: WriteOutcome[] = [];
  for (const w of plan) {
    if (w.action === "alreadyAssigned") {
      outcomes.push({ dateISO: w.dateISO, kind: "alreadyAssigned" });
      continue;
    }
    const { error } = await call(w);
    outcomes.push(describeOutcome(w.dateISO, error));
  }
  return outcomes;
}

/**
 * The Monday a block defaults to starting on: the NEXT one, never today's.
 *
 * ⚠️ NEXT, EVEN WHEN TODAY IS A MONDAY, and that is the decision rather than an off-by-one. A
 * coach planning a block on Monday morning is planning the week that has already begun only if
 * they say so — and a four-week block silently starting today writes its first day into a day
 * the athlete may already have trained. The picker lets them choose this week; the DEFAULT never
 * does it for them.
 *
 * ⚠️ UTC, like `dateForCell`, so the two cannot disagree by a day across a DST boundary.
 */
export function nextMondayISO(today: Date): string {
  const d = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()));
  const ahead = ((1 - d.getUTCDay() + 7) % 7) || 7;
  return new Date(d.getTime() + ahead * 86_400_000).toISOString().slice(0, 10);
}

/**
 * The sentence §3.3 requires the sheet to print, so a coach is not surprised by the weekly
 * adjustment picking up where their block stops.
 *
 * ⚠️ IT IS A FACT ABOUT THE RUNNER, NOT A PROMISE. `CoachAutoApplyDue.due` reads *every workout
 * given LAST week and not given again THIS week is due one week on* — so while a block is
 * running NOTHING in it is ever due (the root is already given this week), and the runner only
 * resumes after the block's last week. Saying "if it is switched on" is load-bearing: his own
 * row is OFF, and a sentence that assumed otherwise would describe a thing that does not happen.
 */
export function afterTheBlockSentence(weeks: number): string {
  return `After week ${String(weeks)} the weekly adjustment continues from week ${String(weeks)}, if it is switched on for this client.`;
}

/** F15 3.9 — which program week a planned date falls on (1-based), from the block's start Monday. UTC, like `dateForCell`. */
export function weekOfDate(startMondayISO: string, dateISO: string): number {
  const days = Math.round((Date.parse(`${dateISO}T00:00:00Z`) - Date.parse(`${startMondayISO}T00:00:00Z`)) / 86_400_000);
  return Math.floor(days / 7) + 1;
}

/** F15 3.9 — said when the block holds AUTO weeks: nothing is assigned for them now; the weekly adjustment writes each one. */
export function autoWeeksSentence(autoWeeks: number): string | null {
  return autoWeeks === 0 ? null
    : `${autoWeeks === 1 ? "The auto week is" : "Auto weeks are"} written each week from the client's training, if the weekly adjustment is on for them.`;
}
